import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import VideoPlayer from "../components/VideoPlayer";
import { EText, EImg } from "../components/cms/Editable";
import PricingCards from "../components/PricingCards";
import { TIERS, TIER_NAME, TIER_RANK } from "../lib/tiers";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError, mediaUrl } from "../lib/api";

export default function Packages() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, setUserDirect } = useAuth();
  const [checkoutTier, setCheckoutTier] = useState(null);
  const [consent, setConsent] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const t = params.get("tier");
    if (params.get("checkout") === "true" && t && t !== "silver") setCheckoutTier(t);
  }, [params]);

  const startCheckout = (tier) => {
    if (tier === "silver") { navigate(user ? "/dashboard" : "/signup"); return; }
    if (!user) { navigate("/login", { state: { from: `/packages?tier=${tier}&checkout=true` } }); return; }
    if (TIER_RANK[user.tier] >= TIER_RANK[tier]) { toast.info("You already have this tier or higher."); return; }
    setCheckoutTier(tier);
  };

  const confirmPurchase = async () => {
    if (!consent) { toast.error("Please accept the disclaimer & terms."); return; }
    setProcessing(true);
    try {
      const { data } = await api.post("/purchase", { tier: checkoutTier });
      setUserDirect(data.user);
      toast.success(`Welcome to ${TIER_NAME[checkoutTier]}!`);
      navigate("/thank-you");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setProcessing(false); }
  };

  const tierObj = TIERS.find((t) => t.id === checkoutTier);

  return (
    <Layout>
      <section className="section-pad">
        <div className="text-center mb-12">
          <EText k="packages.pill" className="pill bg-brand-cream2 text-brand-tan">Lifetime Launch Pricing</EText>
          <EText k="packages.title" as="h1" className="font-display text-4xl sm:text-5xl font-semibold text-brand-ink mt-3 block">Membership Packages</EText>
          <EText k="packages.subtitle" as="p" multiline className="mt-3 text-brand-inksoft max-w-2xl mx-auto block">One-time payment. Lifetime access. Cumulative tiers — everything a lower tier sees, a higher tier unlocks too.</EText>
        </div>
        <div className="grid lg:grid-cols-[200px_minmax(0,1fr)_200px] gap-6 items-stretch max-w-[1500px] mx-auto">
          <EImg k="packages.side.left" src="/api/media/1ad58f8c-12d3-43c0-998a-31835ee70804" alt="Five Elements cycle"
            className="hidden lg:block w-full h-full min-h-[400px] object-cover rounded-2xl" wrapperClassName="relative h-full" />
          <PricingCards onSelect={startCheckout} />
          <EImg k="packages.side.right" src="/api/media/fae76f84-9a1b-427b-bc62-710be86a425e" alt="One with Earth meditation"
            className="hidden lg:block w-full h-full min-h-[400px] object-cover rounded-2xl" wrapperClassName="relative h-full" />
        </div>

        <p className="text-center text-xs text-brand-muted mt-10 max-w-xl mx-auto">
          <ShieldCheck size={14} className="inline mr-1 text-brand-sage" />
          Secure checkout. Payments are processed by a PCI-compliant provider — we never store your card details.
        </p>
      </section>

      <VideosByTier />

      {/* Checkout modal (payment deferred → demo grant) */}
      {checkoutTier && tierObj && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-5" onClick={() => setCheckoutTier(null)}>
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="card-surface p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="checkout-modal">
            <h3 className="font-display text-3xl font-semibold text-brand-ink">{tierObj.name}</h3>
            <div className="flex items-end gap-2 mt-1">
              <span className="font-display text-4xl font-semibold">{tierObj.price}</span>
              <span className="text-brand-muted mb-1.5">one-time · lifetime</span>
            </div>
            <ul className="mt-5 space-y-2">
              {tierObj.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-brand-inksoft"><Check size={16} className="text-brand-sage mt-0.5 shrink-0" />{f}</li>
              ))}
            </ul>
            <div className="mt-5 rounded-xl bg-brand-cream2 p-3 text-xs text-brand-inksoft">
              <strong>Note:</strong> Card payment will be enabled once your payment provider is connected. For now, confirming activates your {tierObj.name} access instantly.
            </div>
            <label className="flex items-start gap-2 text-xs text-brand-inksoft mt-4" data-testid="checkout-consent">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-brand-sage" />
              <span>I accept the medical disclaimer, Terms, and understand this is a one-time lifetime purchase.</span>
            </label>
            <button onClick={confirmPurchase} disabled={processing} data-testid="confirm-purchase"
              className={`mt-5 w-full ${checkoutTier === "diamond" ? "btn-gold" : "btn-primary"}`}>
              {processing ? "Activating…" : `Confirm & Unlock ${tierObj.name}`}
            </button>
            <button onClick={() => setCheckoutTier(null)} className="mt-2 w-full text-sm text-brand-muted">Cancel</button>
          </motion.div>
        </div>
      )}
    </Layout>
  );
}

// All 23 lesson videos embedded per package tier. Videos at or below the
// visitor's tier play right here; higher tiers show a locked teaser.
function VideosByTier() {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    api.get("/content").then((r) => setVideos(r.data.filter((i) => i.type === "video"))).catch(() => {});
  }, []);

  const groups = ["silver", "gold", "diamond"].map((t) => ({
    tier: t,
    name: TIER_NAME[t],
    items: videos.filter((v) => v.required_tier === t),
  }));

  return (
    <section className="section-pad bg-white" data-testid="package-videos">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-4">
          <EText k="packages.videos.pill" className="pill bg-brand-cream2 text-brand-tan">Video Library</EText>
          <EText k="packages.videos.title" as="h2" className="font-display text-3xl sm:text-4xl font-semibold text-brand-ink mt-3 block">What's Inside Each Package</EText>
          <EText k="packages.videos.subtitle" as="p" multiline className="mt-2 text-brand-inksoft max-w-2xl mx-auto block">Every lesson video in each tier. Members press play and watch right here — no downloads, no sharing of raw links.</EText>
        </div>

        {groups.map((g) => (
          <div key={g.tier} className="mt-12" data-testid={`videos-${g.tier}`}>
            <div className="flex items-baseline gap-3 mb-5">
              <h3 className="font-display text-2xl font-semibold text-brand-ink">{g.name}</h3>
              <span className="text-sm text-brand-muted">{g.items.length} video{g.items.length === 1 ? "" : "s"}</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {g.items.map((v) => (
                <div key={v.id} className="card-surface overflow-hidden" data-testid={`pkg-video-${v.id}`}>
                  {v.unlocked ? (
                    <div className="aspect-video bg-black">
                      {v.embed_url?.startsWith("/api/media/") ? (
                        <VideoPlayer itemId={v.id} embedUrl={v.embed_url} sourceUrl={v.source_url} title={v.title} autoPlay={false} />
                      ) : v.embed_url ? (
                        <iframe src={`${v.embed_url}?badge=0&autopause=0&title=0&byline=0&portrait=0&dnt=1`}
                          title={v.title} className="w-full h-full" frameBorder="0"
                          allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
                      ) : (
                        <a href={v.source_url} target="_blank" rel="noreferrer"
                          className="w-full h-full flex flex-col items-center justify-center text-white/80 text-sm px-4 text-center hover:text-white">
                          <Lock size={18} className="text-brand-gold mb-2" />
                          Watch this lesson on isoharts.com →
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="relative aspect-video">
                      <img src={mediaUrl(v.thumbnail)} alt={v.title} className="w-full h-full object-cover blur-sm scale-105" loading="lazy" />
                      <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white text-center p-3">
                        <Lock size={20} className="text-brand-gold" />
                        <span className="mt-2 text-xs uppercase tracking-wide">Locked · {TIER_NAME[v.required_tier]}</span>
                        <button onClick={() => navigate(`/packages?tier=${v.required_tier}&checkout=true`)}
                          className="mt-3 rounded-full bg-brand-gold hover:bg-brand-golddark px-4 py-1.5 text-xs font-semibold"
                          data-testid={`pkg-unlock-${v.id}`}>
                          Unlock with {TIER_NAME[v.required_tier].split(" ")[0]}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="p-3">
                    <h4 className="text-sm font-medium text-brand-ink leading-snug">{v.title}</h4>
                    <span className="text-[11px] uppercase tracking-wide text-brand-muted">{v.category}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
